<template>
    <div class="general-container">
      <div v-if="loading" class="loading-indicator-gcn">Cargando datos generales...</div>
      <div v-if="error && !loading" class="error-message-gcn">{{ error }}</div>
  
      <div v-if="!loading && !error" class="charts-grid">
  
        <!-- Row 1: Índice de Cartera y Análisis (full width) -->
        <div v-if="!isUserBronzeOnly" class="chart-wrapper portfolio-analysis-section"> <!-- Removed layout-span-2 -->
            <h3>Índice de Cartera y Análisis</h3>
            <div class="portfolio-index-layout"> <!-- NEW LAYOUT WRAPPER -->
                <div class="portfolio-index-left-panel"> <!-- NEW LEFT PANEL -->
                    <!-- Summary Table for Portfolio Index -->
                    <div v-if="portfolioIndexData && typeof portfolioIndexData.profit === 'number' && typeof portfolioIndexData.max_drawdown === 'number'" class="portfolio-section">
                        <h4>Cartera Base</h4>
                        <div class="table-wrapper">
                            <table class="data-table compact-table">
                                <thead>
                                    <tr>
                                        <th v-if="portfolioIndexWithAnalysisSymbol" colspan="2" class="text-center">Cartera + {{ portfolioIndexWithAnalysisSymbol }}</th>
                                    </tr>
                                    <tr>
                                        <th class="text-center">Rentabilidad</th>
                                        <th class="text-center">Max. Drawdown</th>
                                        <th v-if="portfolioIndexWithAnalysisSymbol" class="text-center">Rentabilidad</th>
                                        <th v-if="portfolioIndexWithAnalysisSymbol" class="text-center">Max. Drawdown</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td class="text-center" :class="colorProfitIndex(portfolioIndexData.profit)">{{ formatPercentage(portfolioIndexData.profit) }}</td>
                                        <td class="text-center text-danger">{{ formatPercentage(portfolioIndexData.max_drawdown) }}</td>
                                        <td v-if="portfolioIndexWithAnalysisSymbol" class="text-center" :class="colorProfitIndex(portfolioIndexData.profit_analysis)">{{ formatPercentage(portfolioIndexData.profit_analysis) }}</td>
                                        <td v-if="portfolioIndexWithAnalysisSymbol" class="text-center text-danger">{{ formatPercentage(portfolioIndexData.max_drawdown_analysis) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Ticker Search for Analysis (Gold/Platinum users) -->
                    <div class="portfolio-section premium-section premium-gold" :class="{'premium-disabled': !isUserGoldOrPlatinum}">
                        <h4>
                            <span class="premium-badge premium-badge-gold">
                                <span class="material-icons-outlined">star</span>
                                GOLD
                            </span>
                            Analizar con Ticker
                        </h4>
                        <div v-if="!isUserGoldOrPlatinum" class="premium-upgrade-message">
                            <p><strong>🚀 Función Premium:</strong> Analiza el impacto de agregar cualquier ticker a tu cartera. Descubre cómo mejoraría tu rentabilidad y drawdown.</p>
                        </div>
                        <div class="ticker-search-container">
                            <div class="ticker-input-controls">
                                <input 
                                    type="text" 
                                    id="ticker-search" 
                                    v-model="tickerSearchQuery" 
                                    @input="handleTickerSearchDebounced"
                                    placeholder="Buscar ticker..."
                                    list="ticker-suggestions"
                                    :disabled="!isUserGoldOrPlatinum || complementaryValuesProcessing || tickerAnalysisProcessing"
                                    :class="{'input-error': tickerAnalysisStatus.type === 'error'}" />
                                <datalist id="ticker-suggestions">
                                    <option v-for="opt in tickerOptions" :key="opt.title" :value="opt.title">{{ opt.description }}</option>
                                </datalist>
                            </div>
                        </div>
                        
                        <div class="ticker-button-group">
                            <button 
                                type="button" 
                                class="btnGcn btnGcn-primary" 
                                @click="selectSearchedTickerForAnalysis" 
                                :disabled="!isUserGoldOrPlatinum || !tickerSearchQuery.trim() || complementaryValuesProcessing || tickerAnalysisProcessing">
                                <span v-if="tickerAnalysisProcessing" class="material-icons-outlined icon-spin" style="font-size:18px;">hourglass_empty</span>
                                <span v-else class="material-icons-outlined" style="font-size:18px;">add_chart</span>
                                {{ tickerAnalysisProcessing ? 'Analizando...' : 'Analizar' }}
                            </button>
                             <button 
                                type="button" 
                                class="btnGcn btnGcn-secondary" 
                                @click="clearPortfolioIndexAnalysis" 
                                v-if="portfolioIndexWithAnalysisSymbol" 
                                :disabled="!isUserGoldOrPlatinum || complementaryValuesProcessing || tickerAnalysisProcessing">
                                <span v-if="tickerAnalysisProcessing" class="material-icons-outlined icon-spin" style="font-size:18px;">hourglass_empty</span>
                                <span v-else class="material-icons-outlined" style="font-size:18px;">clear</span>
                                {{ tickerAnalysisProcessing ? 'Limpiando...' : 'Limpiar Análisis' }}
                            </button>
                        </div>
                        
                        <!-- Feedback for Ticker Analysis -->
                        <div v-if="tickerAnalysisStatus.message" 
                             :class="['action-feedback', tickerAnalysisStatus.type]" 
                             role="alert">
                            {{ tickerAnalysisStatus.message }}
                        </div>
                    </div>

                    <!-- Optimize Portfolio Button & Progress (Platino users) -->
                    <div class="portfolio-section premium-section premium-platinum" :class="{'premium-disabled': !userPlatino}">
                        <h4>
                            <span class="premium-badge premium-badge-platinum">
                                <span class="material-icons-outlined">diamond</span>
                                PLATINO
                            </span>
                            Optimización de Cartera
                        </h4>
                        <div v-if="!userPlatino" class="premium-upgrade-message">
                            <p><strong>💎 Función Exclusiva:</strong> Descubre los mejores tickers complementarios para optimizar tu cartera. Algoritmo avanzado que analiza correlaciones y maximiza el ratio beneficio/drawdown.</p>
                        </div>
                        <div class="optimize-portfolio-controls">
                            <button 
                                type="button"
                                class="btnGcn btnGcn-primary"
                                :class="{ 
                                    'btnGcn-processing': complementaryValuesProcessing,
                                    'btnGcn-completed': complementaryValuesCalculated && complementaryValues.length > 0 && !complementaryValuesProcessing
                                }"
                                @click="handleOptimizePortfolio"
                                :disabled="!userPlatino || notHasOpenPositions || complementaryValuesProcessing || (complementaryValuesCalculated && complementaryValues.length > 0)">
                                <span v-if="complementaryValuesProcessing"><span class="material-icons-outlined icon-spin">hourglass_empty</span> Optimizando...</span>
                                <span v-else-if="complementaryValuesCalculated && complementaryValues.length > 0"><span class="material-icons-outlined">check_circle</span> Optimizado</span>
                                <span v-else><span class="material-icons-outlined">auto_awesome</span> Optimizar Cartera</span>
                            </button>
                            <div v-if="complementaryValuesProcessing" class="progress-bar-container" style="margin-top: 8px;">
                                <div class="progress-bar" :style="{ width: progressBarWidth + '%' }"></div>
                            </div>
                        </div>
                        <div v-if="notHasOpenPositions" class="no-data-message small-text" style="font-size: 0.8em; text-align:left; padding: 5px 0;">
                            La optimización requiere al menos una posición abierta en la cartera.
                        </div>
                    </div>
                </div> <!-- END NEW LEFT PANEL -->

                <div class="portfolio-index-right-panel"> <!-- NEW RIGHT PANEL -->
                    <!-- Portfolio Index Chart -->
                    <div v-if="portfolioIndexData" :id="chartIdPortfolioIndex" class="chart-container"></div>
                    <div v-else class="no-data-message">Acceso denegado o datos de índice no disponibles.</div>
                </div> <!-- END NEW RIGHT PANEL -->
            </div> <!-- END NEW LAYOUT WRAPPER -->
            
            <!-- Complementary Values Loading & Table (Platino users) - Moved below chart -->
            <div v-if="(complementaryValuesProcessing || (complementaryValuesCalculated && sortedComplementaryValues.length > 0))" class="complementary-values-wrapper" :class="{'premium-disabled': !userPlatino}">
                <div class="complementary-values-header">
                    <h4>Tickers Complementarios Sugeridos</h4>
                    <span v-if="!complementaryValuesProcessing" class="complementary-values-count">{{ sortedComplementaryValues.length }} encontrados</span>
                    <span v-else class="complementary-values-count">Cargando...</span>
                </div>
                <div class="complementary-values-table-wrapper" style="overflow-x: auto;">
                    <table class="data-table compact-table">
                        <thead>
                            <tr>
                                <th @click="handleSortComplementaryTable('ticker')" class="sortable col-ticker">Ticker <span class="sort-icon">{{ getSortIcon('ticker') }}</span></th>
                                <th @click="handleSortComplementaryTable('tipus_entrada_display')" class="sortable col-type">Tipo <span class="sort-icon">{{ getSortIcon('tipus_entrada_display') }}</span></th>
                                <th @click="handleSortComplementaryTable('ratio_profit_drawdown')" class="sortable text-right col-ratio">Ratio <span class="sort-icon">{{ getSortIcon('ratio_profit_drawdown') }}</span></th>
                                <th @click="handleSortComplementaryTable('profit_analysis')" class="sortable text-right col-profit">Bº <span class="sort-icon">{{ getSortIcon('profit_analysis') }}</span></th>
                                <th @click="handleSortComplementaryTable('max_drawdown_analysis')" class="sortable text-right col-dd">DD <span class="sort-icon">{{ getSortIcon('max_drawdown_analysis') }}</span></th>
                                <th class="col-action">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="complementaryValuesProcessing && sortedComplementaryValues.length === 0">
                                <td colspan="6" class="text-center" style="padding: 20px; font-style: italic; color: var(--color-text-muted);">
                                    Cargando resultados...
                                </td>
                            </tr>
                            <tr v-for="value in sortedComplementaryValues" :key="value.id">
                                <td><a href="#" @click.prevent="selectComplementaryForAnalysis(value)" title="Analizar este ticker">{{ value.ticker }}</a></td>
                                <td>{{ value.tipus_entrada_display }}</td>
                                <td :class="['text-right', colorProfitIndex(value.ratio_profit_drawdown)]">{{ value.ratio_profit_drawdown ? value.ratio_profit_drawdown.toFixed(3) : 'N/A' }}</td>
                                <td :class="['text-right', colorProfitIndex(value.profit_analysis)]">{{ formatPercentage(value.profit_analysis) }}</td>
                                <td :class="['text-right', 'text-danger']">{{ formatPercentage(value.max_drawdown_analysis) }}</td>
                                <td class="text-center">
                                    <button 
                                        type="button"
                                        class="btnGcn btnGcn-link btnGcn-sm" 
                                        @click="selectComplementaryForAnalysis(value)" 
                                        title="Ver impacto en gráfico">
                                        <span class="material-icons-outlined" style="font-size:18px;">leaderboard</span>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
  
        <!-- Bronze Upgrade Message for Portfolio Index -->
        <div v-if="isUserBronzeOnly" class="chart-wrapper portfolio-analysis-section premium-disabled">
            <h3>Índice de Cartera y Análisis</h3>
            <div class="premium-upgrade-message">
                <p><strong>🚀 Actualiza tu Plan:</strong> Accede al índice de cartera, análisis con tickers y herramientas avanzadas de optimización. Disponible en planes superiores.</p>
            </div>
            <div class="no-data-message">
                Esta función requiere un plan superior al Bronze.
            </div>
        </div>
  
        <!-- Row 2, 3, 4: Capital Curve, Risk, Cycles (grouped into one row) -->
        <div class="row-wrapper-for-children row-3-items-per-row">
            <!-- Capital Curve (was Row 2) -->
            <div class="chart-wrapper">
              <h3>Curva de Capital</h3>
              <div v-if="capitalCurveData" :id="chartIdCapitalCurve" class="chart-container"></div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>
      
            <!-- Risk (was Row 3) -->
             <div class="chart-wrapper">
              <h3>Riesgo Actual</h3>
              <div v-if="riskData" :id="chartIdRisk" class="chart-container chart-container-small"></div>
              <div v-else-if="isUserBronzeOnly" class="no-data-message">
                <p><strong>📊 Datos de Riesgo:</strong> Este módulo está disponible para usuarios Bronze, pero los datos no están disponibles actualmente.</p>
                <p><em>Esto podría deberse a que la cartera no tiene posiciones abiertas o necesita más datos históricos.</em></p>
              </div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>
      
            <!-- Cycles (was Row 4) -->
             <div v-if="!isUserBronzeOnly" class="chart-wrapper">
              <h3>Ciclos Bº/Pº</h3>
               <div v-if="cyclesData" :id="chartIdCycles" class="chart-container chart-container-medium"></div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>

            <!-- Bronze Upgrade Message for Cycles -->
            <div v-if="isUserBronzeOnly" class="chart-wrapper premium-disabled">
                <h3>Ciclos Bº/Pº</h3>
                <div class="premium-upgrade-message">
                    <p><strong>📊 Plan Superior:</strong> Analiza los ciclos de beneficios y pérdidas de tu cartera para mejorar tu estrategia de trading.</p>
                </div>
                <div class="no-data-message">
                    Esta función requiere un plan superior al Bronze.
                </div>
            </div>
        </div>

        <!-- Row 5: Cobertura de Divisa (Table) & Diversificación (Divisa) (Chart) - (half each) -->
        <div v-if="!isUserBronzeOnly" class="row-wrapper-for-children row-2-items-per-row">
            <!-- Currency Coverage Table -->
            <div class="chart-wrapper" v-if="coveragesData && coveragesData.coverages && coveragesData.coverages.length > 0"> <!-- Removed layout-span-2 -->
              <h3>Cobertura de Divisa</h3>
              <div class="table-wrapper">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Divisa</th>
                      <th v-if="coveragesData.full_coverages_data">Ud. Tiene</th>
                      <th>Cobertura</th>
                      <th v-if="coveragesData.full_coverages_data">Total Pendiente</th>
                      <th v-if="isUserGoldOrPlatinum && coveragesData.full_coverages_data">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(coverage, index) in coveragesData.coverages" :key="index">
                      <td>{{ coverage.currency }}</td>
                      <td v-if="coveragesData.full_coverages_data">
                        <input 
                          type="text" 
                          inputmode="decimal"
                          v-model="coverage.coverage_done_editing" 
                          @input="prepareCoverageUpdate(coverage, $event)"
                          :disabled="!isUserGoldOrPlatinum" 
                          style="width: 100px;"
                          step="0.01"
                          title="Cantidad cubierta que posee"
                        />
                      </td>
                      <td class="text-right">{{ formatCurrencyRight(coverage.coverage, portfolioCurrency) }}</td>
                      <td v-if="coveragesData.full_coverages_data" class="text-right">{{ formatCurrencyRight(coverage.pending_coverage, portfolioCurrency) }}</td>
                      <td v-if="isUserGoldOrPlatinum && coveragesData.full_coverages_data" class="text-center">
                        <button 
                          class="btnGcn btnGcn-primary btnGcn-sm" 
                          @click="executeUpdateCoverage(coverage)" 
                          :disabled="!coverage.hasChanged || coverage.isUpdating"
                          title="Guardar cambio en cobertura"
                        >
                           <span v-if="coverage.isUpdating">Guardando...</span>
                           <span v-else class="material-icons-outlined" style="font-size:18px;">save</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Feedback for Coverage Update -->
              <div v-if="coverageUpdateStatus.message" 
                   :class="['action-feedback', coverageUpdateStatus.type]" 
                   role="alert">
                  {{ coverageUpdateStatus.message }}
              </div>
              <div v-if="!coveragesData.full_coverages_data" class="premium-upgrade-message small-text">
                <p><strong>⭐ Función Gold/Platino:</strong> Información detallada y edición de coberturas de divisa disponible para usuarios premium.</p>
              </div>
            </div>
            <!-- Currency Diversification Chart -->
            <div class="chart-wrapper">
              <h3>Diversificación (Divisa)</h3>
              <div v-if="currencyDiverData" :id="chartIdCurrencyDiver" class="chart-container chart-container-small"></div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>
        </div>

        <!-- Bronze Upgrade Message for Currency Features -->
        <div v-if="isUserBronzeOnly" class="row-wrapper-for-children row-2-items-per-row">
            <div class="chart-wrapper premium-disabled">
                <h3>Cobertura de Divisa</h3>
                <div class="premium-upgrade-message">
                    <p><strong>💱 Plan Superior:</strong> Gestiona y edita las coberturas de divisa de tu cartera para optimizar el riesgo cambiario.</p>
                </div>
                <div class="no-data-message">
                    Esta función requiere un plan superior al Bronze.
                </div>
            </div>
            <div class="chart-wrapper premium-disabled">
                <h3>Diversificación (Divisa)</h3>
                <div class="premium-upgrade-message">
                    <p><strong>🌍 Plan Superior:</strong> Visualiza la diversificación por divisas de tu cartera para reducir riesgos geográficos.</p>
                </div>
                <div class="no-data-message">
                    Esta función requiere un plan superior al Bronze.
                </div>
            </div>
        </div>
  
        <!-- Row 6: Investment Distribution (full width) -->
         <div v-if="!isUserBronzeOnly" class="investment-distribution chart-wrapper"> <!-- Removed layout-span-2 -->
             <h3>Distribución Inversión</h3>
             <!-- Chart for Investment Distribution -->
             <div v-if="investmentData" :id="chartIdInvestment" class="chart-container chart-container-medium"></div>

             <!-- Textual details for Investment Distribution -->
              <div v-if="investmentData" class="investment-summary-details">
                  <p><strong>Moneda Base:</strong> {{ investmentData.currency }}</p>
                  <div class="investment-row">
                      <div><strong>Total Acciones:</strong> {{ formatPercentage(investmentData.stock_total) }}</div>
                      <div><strong>Total CFDs:</strong> {{ formatPercentage(investmentData.cfd_total) }}</div>
                      <div><strong>Total Efectivo:</strong> {{ formatPercentage(investmentData.cash_total) }}</div>
                  </div>
                   <hr>
                   <div class="investment-row">
                       <div><strong>Acciones (Invertido):</strong> {{ formatPercentage(investmentData.stock_investment) }} ({{ formatCurrencyRight(investmentData.stock_investment_absolute, investmentData.currency) }})</div>
                       <div><strong>Acciones (Libre):</strong> {{ formatPercentage(investmentData.stock_free) }} ({{ formatCurrencyRight(investmentData.stock_free_absolute, investmentData.currency) }})</div>
                   </div>
                   <div class="investment-row">
                       <div><strong>CFDs (Invertido):</strong> {{ formatPercentage(investmentData.cfd_investment) }} ({{ formatCurrencyRight(investmentData.cfd_investment_absolute, investmentData.currency) }})</div>
                       <div><strong>CFDs (Libre):</strong> {{ formatPercentage(investmentData.cfd_free) }} ({{ formatCurrencyRight(investmentData.cfd_free_absolute, investmentData.currency) }})</div>
                   </div>
                   <div class="investment-row">
                       <div><strong>Efectivo (Cubierto):</strong> {{ formatPercentage(investmentData.cash_investment) }} ({{ formatCurrencyRight(investmentData.cash_investment_absolute, investmentData.currency) }})</div>
                       <div><strong>Efectivo (Libre):</strong> {{ formatPercentage(investmentData.cash_free) }} ({{ formatCurrencyRight(investmentData.cash_free_absolute, investmentData.currency) }})</div>
                   </div>

              </div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
         </div>

         <!-- Bronze Upgrade Message for Investment Distribution -->
         <div v-if="isUserBronzeOnly" class="investment-distribution chart-wrapper premium-disabled">
             <h3>Distribución Inversión</h3>
             <div class="premium-upgrade-message">
                 <p><strong>📈 Plan Superior:</strong> Analiza la distribución de tu inversión entre acciones, CFDs y efectivo con gráficos detallados.</p>
             </div>
             <div class="no-data-message">
                 Esta función requiere un plan superior al Bronze.
             </div>
         </div>

        <!-- Row 7: Diversificación (Beneficio, Sectores, Países) - (third each) -->
        <div v-if="!isUserBronzeOnly" class="row-wrapper-for-children row-3-items-per-row">
            <!-- Profit Diversification -->
             <div class="chart-wrapper">
              <h3>Diversificación (Beneficio)</h3>
               <div v-if="profitDiverData" :id="chartIdProfitDiver" class="chart-container chart-container-small"></div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>
      
            <!-- Sectors Diversification -->
             <div class="chart-wrapper">
              <h3>Diversificación (Sectores)</h3>
               <div v-if="sectorsData" :id="chartIdSectors" class="chart-container chart-container-small"></div>
               <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>
      
            <!-- Countries Diversification -->
            <div class="chart-wrapper">
              <h3>Diversificación (Países)</h3>
              <div v-if="countriesData" :id="chartIdCountries" class="chart-container chart-container-small"></div>
              <div v-else class="no-data-message">Acceso denegado o datos no disponibles.</div>
            </div>
        </div>

        <!-- Bronze Upgrade Message for Diversification Features -->
        <div v-if="isUserBronzeOnly" class="row-wrapper-for-children row-3-items-per-row">
            <div class="chart-wrapper premium-disabled">
                <h3>Diversificación (Beneficio)</h3>
                <div class="premium-upgrade-message">
                    <p><strong>💰 Plan Superior:</strong> Analiza la diversificación por rangos de beneficio para equilibrar tu cartera.</p>
                </div>
                <div class="no-data-message">
                    Esta función requiere un plan superior al Bronze.
                </div>
            </div>
            <div class="chart-wrapper premium-disabled">
                <h3>Diversificación (Sectores)</h3>
                <div class="premium-upgrade-message">
                    <p><strong>🏭 Plan Superior:</strong> Visualiza la distribución sectorial de tu cartera para reducir riesgos específicos del sector.</p>
                </div>
                <div class="no-data-message">
                    Esta función requiere un plan superior al Bronze.
                </div>
            </div>
            <div class="chart-wrapper premium-disabled">
                <h3>Diversificación (Países)</h3>
                <div class="premium-upgrade-message">
                    <p><strong>🌎 Plan Superior:</strong> Analiza la diversificación geográfica de tu cartera para minimizar riesgos país.</p>
                </div>
                <div class="no-data-message">
                    Esta función requiere un plan superior al Bronze.
                </div>
            </div>
        </div>
  
      </div>
       <div v-if="!loading && !error && !hasAnyData" class="no-data-message full-width">
          No hay datos generales disponibles para esta cartera o no tiene permisos para verlos.
      </div>
    </div>
  </template>
  
  <script>
  import apiService, { formatCurrency } from './apiService';
  import { mapGetters } from 'vuex';
  import Highcharts from 'highcharts';
  // Optionally import Highcharts modules like exporting, stock, etc. if needed
  // import Exporting from 'highcharts/modules/exporting';
  // Exporting(Highcharts);
  
  // --- Highcharts Global Options ---
  // Set common options for a sleek/modern dark look
  Highcharts.setOptions({
      chart: {
          backgroundColor: 'transparent', // var(--color-surface, '#20202B') handled by wrapper usually
          style: {
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              color: 'var(--color-text-primary, #E0E0E0)'
          },
          plotBorderColor: 'var(--color-outline-strong, #555)'
      },
      title: { 
          text: null,
          style: { color: 'var(--color-text-primary, #E0E0E0)' }
      },
      credits: { enabled: false },
      xAxis: {
          lineColor: 'var(--color-outline, rgba(255, 255, 255, 0.3))',
          tickColor: 'var(--color-outline, rgba(255, 255, 255, 0.3))',
          labels: { style: { color: 'var(--color-text-secondary, #BFC2CD)' } },
          title: { style: { color: 'var(--color-text-secondary, #BFC2CD)' } }
      },
      yAxis: {
          title: { text: null, style: { color: 'var(--color-text-secondary, #BFC2CD)' } },
          gridLineColor: 'var(--color-outline-soft, rgba(255, 255, 255, 0.15))',
          labels: { style: { color: 'var(--color-text-secondary, #BFC2CD)' } },
          lineColor: 'var(--color-outline, rgba(255, 255, 255, 0.3))',
          tickColor: 'var(--color-outline, rgba(255, 255, 255, 0.3))'
      },
      tooltip: {
          backgroundColor: 'rgba(30, 30, 42, 0.92)', // Darker, slightly transparent
          borderColor: 'var(--color-outline-strong, rgba(255, 255, 255, 0.4))',
          borderWidth: 1,
          shadow: true, // Keep shadow for depth
          style: { color: 'var(--color-text-primary-tooltip, #F0F0F0)' }, // Brighter text for tooltip
          shared: true,
          crosshairs: true
      },
      legend: {
          itemStyle: { color: 'var(--color-text-secondary, #BFC2CD)' },
          itemHoverStyle: { color: 'var(--color-text-primary, #FFFFFF)' },
          itemHiddenStyle: { color: 'var(--color-text-muted, #777)' },
          title: { style: { color: 'var(--color-text-secondary, #BFC2CD)' } }
      },
      plotOptions: {
          series: {
              animation: { duration: 600 },
              marker: { 
                  enabled: false,
                  states: {
                      hover: { enabled: true, radius: 4, lineWidth: 1, lineColor: 'rgba(255,255,255,0.7)'}
                  }
              },
              states: {
                  hover: { lineWidthPlus: 1 },
                  inactive: { opacity: 0.5 }
              },
              borderColor: 'var(--color-surface, #20202B)', // For column, bar, pie borders
          },
          line: { 
              lineWidth: 2,
              // color property will come from the colors array or series specific config
          },
          pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              dataLabels: {
                  enabled: true,
                  format: '<b>{point.name}</b><br>{point.percentage:.1f}%',
                   style: {
                       color: 'var(--color-text-data-label, #D0D0D0)', // Slightly dimmer for data labels
                       textOutline: 'none', // Remove default outline for better readability
                       fontWeight: 'normal',
                       textAlign: 'center'
                   }
              },
             borderWidth: 2 // Add a slight border to pie slices, color from series.borderColor or plotOptions.series.borderColor
          },
          column: {
              borderWidth: 0, // Keep if rounded, or small like 1px
              borderRadius: 3, // Slightly rounded columns
              // colorByPoint: false // Usually false, colors from series
              dataLabels: {
                  style: {
                      color: 'var(--color-text-data-label, #D0D0D0)',
                      textOutline: 'none',
                      fontWeight: 'normal'
                  }
              }
          }
      },
      // Dark theme friendly color palette (Highcharts default dark-unica palette inspired)
      colors: ['#2b908f', '#90ee7e', '#f45b5b', '#7798BF', '#aaeeee', '#ff0066', '#eeaaee', '#55BF3B', '#DF5353', '#7798BF', '#aaeeee']
  });
  // --- End Highcharts Global Options ---
  
  
  export default {
    name: 'General',
    props: {
      portfolioId: { type: String, required: true },
    },
    data() {
      return {
        loading: false,
        error: null,
        portfolioDetails: null, // For general portfolio info like currency, name, open_positions.total_amount
        portfolioCurrency: 'EUR', // Default, updated from portfolioDetails or investmentData
  
        // Data holders
        capitalCurveData: null,
        portfolioIndexData: null, // Will also store analysis specific data like profit_analysis, max_drawdown_analysis
        riskData: null,
        cyclesData: null,
        currencyDiverData: null,
        profitDiverData: null,
        investmentData: null,
        sectorsData: null,
        countriesData: null,
        coveragesData: null, // To store { coverages: [], full_coverages_data: boolean }
        
        // User roles now handled by Vuex store (_g_UserFeatures)

        // Portfolio Index Analysis
        portfolioIndexWithAnalysisSymbol: null,
        tickerSearchQuery: '',
        tickerOptions: [], // For autocomplete
        selectedAnalysisStock: null, // Store { id: analysis_id, ticker: symbol }
        tickerAnalysisProcessing: false, // Track analysis state for loading indicators
        tickerAnalysisStatus: { message: '', type: '' }, // For user feedback

        // Optimize Portfolio Feature
        complementaryValues: [],
        complementaryValuesCalculated: false,
        complementaryValuesProcessing: false,
        complementaryValuesFull: [], // Raw data from API
        currentSortKeyComplementary: 'ratio_profit_drawdown',
        currentSortDirComplementary: 'desc',
        progressBarWidth: 0,
        optimizePortfolioInterval: null,
        progressBarCounter: 0,
        countTickers: 0,
        fakeTicker: {}, // For showing tickers as they load like legacy


        // Chart instances
        chartInstances: {
            capitalCurve: null,
            portfolioIndex: null,
            risk: null,
            cycles: null,
            currencyDiver: null,
            profitDiver: null,
            investment: null, // Added for the investment pie chart
            sectors: null,
            countries: null,
        },
  
        // Unique IDs for chart containers
        chartIdCapitalCurve: 'hc-general-' + this._uid + '-capital',
        chartIdPortfolioIndex: 'hc-general-' + this._uid + '-index',
        chartIdRisk: 'hc-general-' + this._uid + '-risk',
        chartIdCycles: 'hc-general-' + this._uid + '-cycles',
        chartIdCurrencyDiver: 'hc-general-' + this._uid + '-currency',
        chartIdProfitDiver: 'hc-general-' + this._uid + '-profit',
        chartIdInvestment: 'hc-general-' + this._uid + '-investment', // Added
        chartIdSectors: 'hc-general-' + this._uid + '-sectors',
        chartIdCountries: 'hc-general-' + this._uid + '-countries',

        // Debounce timer for ticker search
        tickerSearchDebounceTimer: null,
        mappingTipusEntrada: { '0': 'RR', '1': 'RRA', '2': 'LT', '3': 'LTA', '6': 'SE', '7': 'MKT' }, // From legacy
        
        // For user feedback messages
        coverageUpdateStatus: { message: '', type: '' }, // type can be 'success' or 'error'
        optimizePortfolioStatus: { message: '', type: ''},
      };
    },
    computed: {
        hasAnyData() {
            // Check if at least one data property has successfully fetched data (is not null/false)
            return !!(this.capitalCurveData || this.portfolioIndexData || this.riskData || this.cyclesData || this.currencyDiverData || this.profitDiverData || this.investmentData || this.sectorsData || this.countriesData || this.coveragesData);
        },
        ...mapGetters(['_g_UserFeatures']),
        isUserGoldOrPlatinum() {
            // Pre Eureker gets the gold portfolio tools.
            return this._g_UserFeatures?.is_gold_user || this._g_UserFeatures?.is_platinum_user || this._g_UserFeatures?.is_pre_eureker_user;
        },
        isUserBronzeOnly() {
            return this._g_UserFeatures?.is_bronze_user && !this._g_UserFeatures?.is_gold_user && !this._g_UserFeatures?.is_platinum_user;
        },
        userPlatino() {
            return this._g_UserFeatures?.is_platinum_user || false;
        },
        userGold() {
            return this._g_UserFeatures?.is_gold_user || false;
        },
        notHasOpenPositions() {
            return !this.portfolioDetails || this.portfolioDetails.open_positions?.total_amount === 0;
        },
        // For complementary values table
        sortedComplementaryValues() {
            if (!this.complementaryValues) return [];
            let sorted = [...this.complementaryValues].sort((a, b) => {
                let modifier = 1;
                if (this.currentSortDirComplementary === 'desc') modifier = -1;
                if (a[this.currentSortKeyComplementary] < b[this.currentSortKeyComplementary]) return -1 * modifier;
                if (a[this.currentSortKeyComplementary] > b[this.currentSortKeyComplementary]) return 1 * modifier;
                return 0;
            });

            // Add fake ticker at the beginning if processing like legacy
            if (this.fakeTicker.ticker !== undefined && this.complementaryValuesProcessing) {
                sorted.splice(0, 0, this.fakeTicker);
            }

            return sorted;
        }
    },
    methods: {
      // --- Anglo Number Formatting Helpers ---
      formatNumberAnglo(value, decimals = 2) {
        if (value === null || value === undefined || value === '') return '-';
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return '-';
        const absValue = Math.abs(num);
        const parts = absValue.toFixed(decimals).split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = parts[1];
        return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      },
      formatCurrency(value, currency = 'EUR') {
        return this.formatCurrencyRight(value, currency);
      },
      formatCurrencyRight(value, currency = 'EUR') {
        if (typeof value !== 'number') return '-';
        const isNegative = value < 0;
        const formattedNumber = this.formatNumberAnglo(Math.abs(value), 2);
        const currencySymbol = currency === 'EUR' ? '€' : currency;
        
        return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
      },
      formatPercentage(value) {
          const num = typeof value === 'number' ? value : Number(value);
          if (isNaN(num)) return 'N/A';
          return `${this.formatNumberAnglo(num, 2)}%`;
      },
      colorProfitIndex(value) {
          if (typeof value !== 'number' || isNaN(value)) return ''; // No class if not a valid number
          return value >= 0 ? 'text-success' : 'text-danger';
      },
      mapTipusEntrada(tipusEntradaValue) {
          return this.mappingTipusEntrada[tipusEntradaValue] || tipusEntradaValue;
      },
      getSortIcon(key) {
          if (this.currentSortKeyComplementary === key) {
              return this.currentSortDirComplementary === 'asc' ? '▲' : '▼';
          }
          return ''; // No icon or a default one like '↕'
      },

      handleSortComplementaryTable(sortKey) {
          if (this.currentSortKeyComplementary === sortKey) {
              this.currentSortDirComplementary = this.currentSortDirComplementary === 'asc' ? 'desc' : 'asc';
          } else {
              this.currentSortKeyComplementary = sortKey;
              this.currentSortDirComplementary = 'desc'; // Default to desc for new column
          }
      },

      handleTickerSearchDebounced() {
          clearTimeout(this.tickerSearchDebounceTimer);
          this.tickerSearchDebounceTimer = setTimeout(() => {
              this.handleTickerSearch();
          }, 300); // 300ms debounce
      },

      async handleTickerSearch() {
          if (this.tickerSearchQuery && this.tickerSearchQuery.length > 1) {
              try {
                  const rawSuggestions = await apiService.getAutocompleteSuggestions(this.tickerSearchQuery);
                  if (rawSuggestions && typeof rawSuggestions === 'object' && !Array.isArray(rawSuggestions)) {
                      // Transform object {key: value} to array [{ title: key, description: value }]
                      this.tickerOptions = Object.entries(rawSuggestions).map(([title, description]) => ({
                          title,
                          description
                      }));
                  } else if (Array.isArray(rawSuggestions)) {
                      // If it's already an array, use it directly or adapt if structure is different
                      // Assuming it might be an array of strings or objects with title/description
                      this.tickerOptions = rawSuggestions.map(opt => {
                          if (typeof opt === 'string') return { title: opt, description: opt };
                          return { title: opt.title || opt.value || '', description: opt.description || opt.text || '' };
                      });
                  } else {
                      this.tickerOptions = [];
                  }
              } catch (err) {
                  console.error("Error fetching ticker suggestions:", err);
                  this.tickerOptions = [];
              }
          } else {
              this.tickerOptions = [];
          }
      },

      async selectSearchedTickerForAnalysis() {
          if (!this.tickerSearchQuery.trim() || this.tickerAnalysisProcessing) return;
          
          this.tickerAnalysisProcessing = true;
          this.tickerAnalysisStatus = { message: '', type: '' }; // Clear previous status
          
          try {
              const query = this.tickerSearchQuery.trim().toUpperCase();
              const result = await apiService.findStockForAnalysis(query);
              
              // Adapt to potential array response, similar to legacy behavior
              // If result is an array and has items, use the first one. Otherwise, use result directly.
              const stockInfo = Array.isArray(result) && result.length > 0 ? result[0] : result;

              if (stockInfo && stockInfo.id && stockInfo.ticker) {
                  await this.performPortfolioIndexAnalysis(stockInfo.ticker, stockInfo.id);
                  // Only set error to null on success if it was previously set from another operation
                  if (this.error) this.error = null; 
                  this.tickerAnalysisStatus = { message: `Análisis con ${stockInfo.ticker} completado.`, type: 'success' };
                  // Clear status message after a delay
                  setTimeout(() => {
                      if (this.tickerAnalysisStatus.type === 'success') {
                          this.tickerAnalysisStatus = { message: '', type: '' };
                      }
                  }, 4000);
              } else {
                  // Don't set global error for ticker-related issues, use tickerAnalysisStatus instead
                  this.tickerAnalysisStatus = { message: `No se encontró información para ${query}.`, type: 'error' };
                  console.warn("No stock info found for analysis using query:", query, "API Result was:", result);
              }
          } catch (err) {
              console.error("Error selecting ticker for analysis:", err);
              const errorMsg = `Error al buscar '${this.tickerSearchQuery.trim().toUpperCase()}': ${err?.message || String(err)}`;
              // Don't set global error for ticker-related issues
              this.tickerAnalysisStatus = { message: errorMsg, type: 'error' };
          } finally {
              this.tickerAnalysisProcessing = false;
          }
      },
      
      async selectComplementaryForAnalysis(stock) {
          if (stock && stock.id && stock.ticker) {
              await this.performPortfolioIndexAnalysis(stock.ticker, stock.id);
          }
      },

      async performPortfolioIndexAnalysis(stockSymbol, stockAnalysisId) {
          if (!stockSymbol || !stockAnalysisId) return;
          let analysisError = null; // Local error tracking for this operation

          try {
              const analysisData = await apiService.getPortfolioIndex(this.portfolioId, stockAnalysisId, stockSymbol);
              if (analysisData) {
                  this.portfolioIndexData = analysisData; 
                  this.portfolioIndexWithAnalysisSymbol = stockSymbol;

                  await new Promise((resolve, reject) => {
                      this.$nextTick(() => {
                          try {
                              this.renderChart('portfolioIndex', this.chartIdPortfolioIndex, this.getPortfolioIndexOptions, this.portfolioIndexData);
                              resolve();
                          } catch (renderErr) {
                              console.error(`Error rendering portfolio index chart for ${stockSymbol} within nextTick:`, renderErr);
                              analysisError = `Error al renderizar gráfico para ${stockSymbol}: ${renderErr.message}`;
                              reject(renderErr);
                          }
                      });
                  });

              } else {
                  analysisError = `No se pudieron obtener los datos de análisis para ${stockSymbol}.`;
              }
          } catch (fetchErr) {
              console.error(`Error fetching portfolio index with analysis for ${stockSymbol}:`, fetchErr);
              analysisError = `Error analizando con ${stockSymbol}: ${fetchErr.message}`;
          } finally {
              // If an error occurred specifically during this analysis, only set tickerAnalysisStatus (not global error)
              if (analysisError) {
                  this.tickerAnalysisStatus = { message: analysisError, type: 'error' };
              }
          }
      },

      async clearPortfolioIndexAnalysis() {
          if (this.tickerAnalysisProcessing) return;

          this.tickerAnalysisProcessing = true;
          this.tickerAnalysisStatus = { message: '', type: '' }; // Clear previous status
          let clearError = null;

          try {
              // Fetch the base portfolio index data again
              const baseIndexData = await apiService.getPortfolioIndex(this.portfolioId);
              if (baseIndexData) {
                  this.portfolioIndexData = baseIndexData;
                  this.portfolioIndexWithAnalysisSymbol = null;

                  await new Promise((resolve, reject) => {
                      this.$nextTick(() => {
                          try {
                              this.renderChart('portfolioIndex', this.chartIdPortfolioIndex, this.getPortfolioIndexOptions, this.portfolioIndexData);
                              resolve();
                          } catch (renderErr) {
                              console.error("Error rendering portfolio index chart after clearing analysis within nextTick:", renderErr);
                              clearError = `Error al renderizar gráfico tras limpiar análisis: ${renderErr.message}`;
                              reject(renderErr);
                          }
                      });
                  });
                  
                  // Show success message
                  this.tickerAnalysisStatus = { message: 'Análisis limpiado correctamente.', type: 'success' };
                  // Clear message after delay
                  setTimeout(() => {
                      if (this.tickerAnalysisStatus.type === 'success') {
                          this.tickerAnalysisStatus = { message: '', type: '' };
                      }
                  }, 4000);
                  
              } else {
                  clearError = "No se pudo recargar el índice de cartera base.";
              }
          } catch (fetchErr) {
              console.error("Error clearing portfolio index analysis (fetching base data):", fetchErr);
              clearError = `Error al limpiar análisis (obteniendo datos base): ${fetchErr.message}`;
          } finally {
              if (clearError) {
                  // Only set local error status, not global error
                  this.tickerAnalysisStatus = { message: clearError, type: 'error' };
              }
              this.tickerAnalysisProcessing = false;
          }
      },

      async handleOptimizePortfolio() {
          if (this.notHasOpenPositions || this.complementaryValuesProcessing) return;

          this.complementaryValuesProcessing = true;
          this.complementaryValuesCalculated = false;
          this.complementaryValues = [];
          this.complementaryValuesFull = [];
          this.progressBarWidth = 0;
          this.progressBarCounter = 0;
          this.countTickers = 0;
          this.fakeTicker = {};
          this.optimizePortfolioStatus = { message: 'Optimizando cartera...', type: 'info' };
          
          // Fake progress bar like legacy - increment every 3 seconds for 16 iterations
          const secondsToFakeProgressBar = 16;
          this.optimizePortfolioInterval = setInterval(() => {
              if (this.progressBarCounter < secondsToFakeProgressBar) {
                  this.progressBarCounter++;
                  this.progressBarWidth = this.progressBarCounter;
              } else {
                  clearInterval(this.optimizePortfolioInterval);
                  this.optimizePortfolioInterval = null;
              }
          }, 3000);

          try {
              const rawData = await apiService.getTickersForMarketAnalysis(this.portfolioId);
              this.complementaryValuesFull = rawData.map(item => ({
                  ...item,
                  tipus_entrada_display: this.mapTipusEntrada(item.tipus_entrada)
              }));
              
              this.complementaryValuesCalculated = true;
              
              // Shuffle like legacy using lodash-style shuffle
              if (this.complementaryValuesFull.length > 0) {
                  // Fisher-Yates shuffle
                  let array = [...this.complementaryValuesFull];
                  for (let i = array.length - 1; i > 0; i--) {
                      const j = Math.floor(Math.random() * (i + 1));
                      [array[i], array[j]] = [array[j], array[i]];
                  }
                  this.complementaryValuesFull = array;
                  
                  // Start incremental loading like legacy
                  const preprocessInterval = setInterval(() => {
                      if (this.countTickers < this.complementaryValuesFull.length) {
                          // Add current ticker to the main array
                          this.complementaryValues.push(this.complementaryValuesFull[this.countTickers]);
                          
                          // Set next ticker as fakeTicker if there is one (for preview effect)
                          if (this.countTickers + 1 < this.complementaryValuesFull.length) {
                              this.fakeTicker = this.complementaryValuesFull[this.countTickers + 1];
                          } else {
                              this.fakeTicker = {}; // Clear fakeTicker when done
                          }
                      } else {
                          // Done processing
                          this.complementaryValuesProcessing = false;
                          this.fakeTicker = {}; // Clear fakeTicker
                          this.optimizePortfolioStatus = { message: '', type: '' }; // Clear status when done
                          clearInterval(preprocessInterval);
                      }

                      // Continue incrementing progress bar beyond fake progress
                      if (this.progressBarWidth < 100) {
                          this.progressBarCounter++;
                          this.progressBarWidth = this.progressBarCounter;
                      }

                      this.countTickers++;
                  }, 350); // 350ms interval like legacy
              } else {
                  // No data case
                  this.complementaryValuesProcessing = false;
                  this.progressBarWidth = 100;
                  this.optimizePortfolioStatus = { message: '', type: '' }; // Clear status when done
              }

          } catch (err) {
              console.error("Error optimizing portfolio:", err);
              this.error = `Error optimizando cartera: ${err.message}`;
              if (this.optimizePortfolioInterval) {
                  clearInterval(this.optimizePortfolioInterval);
                  this.optimizePortfolioInterval = null;
              }
              this.complementaryValuesProcessing = false;
              this.progressBarWidth = 0;
          }
      },
  
      async fetchData() {
        if (!this.portfolioId) {
          this.error = "ID de cartera no proporcionado.";
          return;
        }
        this.loading = true;
        this.error = null;
        this.resetData(); // Clear previous data

        try {
            // Fetch all data concurrently
            const results = await Promise.allSettled([
              apiService.getPortfolioDetails(this.portfolioId), // Fetch general portfolio details
              apiService.getCapitalCurve(this.portfolioId),
              apiService.getPortfolioIndex(this.portfolioId), // Base portfolio index
              apiService.getRiskData(this.portfolioId),
              apiService.getCyclesData(this.portfolioId),
              apiService.getCurrencyDiverData(this.portfolioId),
              apiService.getProfitDiverData(this.portfolioId),
              apiService.getInvestmentData(this.portfolioId),
              apiService.getSectorsDiversificationData(this.portfolioId),
              apiService.getCountriesDiversificationData(this.portfolioId),
              apiService.getCoveragesData(this.portfolioId), // Fetch coverages
            ]);
      
            // Process results
            const [
                portfolioDetailsRes, capitalCurveRes, indexRes, riskRes, cyclesRes, currencyRes,
                profitRes, investmentRes, sectorsRes, countriesRes, coveragesRes
            ] = results;
      
            this.portfolioDetails = this.processResult(portfolioDetailsRes, 'Detalles Cartera');
            this.capitalCurveData = this.processResult(capitalCurveRes, 'Curva Capital');
            this.portfolioIndexData = this.processResult(indexRes, 'Índice Cartera');
            this.riskData = this.processResult(riskRes, 'Datos Riesgo');
            this.cyclesData = this.processResult(cyclesRes, 'Ciclos');
            this.currencyDiverData = this.processResult(currencyRes, 'Div. Divisa');
            this.profitDiverData = this.processResult(profitRes, 'Div. Beneficio');
            this.investmentData = this.processResult(investmentRes, 'Dist. Inversión');
            this.sectorsData = this.processResult(sectorsRes, 'Div. Sectores');
            this.countriesData = this.processResult(countriesRes, 'Div. Países');
            this.coveragesData = this.processResult(coveragesRes, 'Coberturas Divisa');
    
    
            // Update portfolio currency if investment data fetched successfully
             if (this.portfolioDetails && this.portfolioDetails.currency) {
                  this.portfolioCurrency = this.portfolioDetails.currency;
             } else if (this.investmentData && this.investmentData.currency) {
                  this.portfolioCurrency = this.investmentData.currency;
             }
      
            // Render charts after data is processed
            // Ensure this happens only if no major error has occurred before this point
            // or if partial data is acceptable for rendering. Error handling is in processResult.
            // this.$nextTick(() => {
            //   this.renderAllCharts();
            // });

        } catch (err) {
            console.error("Unhandled error in fetchData process:", err);
            this.error = "Ocurrió un error inesperado al cargar los datos generales. Por favor, intente de nuevo.";
            // Ensure any specific loading states are also reset if used elsewhere
        } finally {
            this.loading = false;
            // Render charts after all data processing attempts and loading is false
            this.$nextTick(() => {
                this.renderAllCharts();
            });
        }
      },
  
      processResult(result, name) {
          if (result.status === 'fulfilled' && result.value && result.value !== false) {
              // Special handling for coveragesData to add editing fields
              if (name === 'Coberturas Divisa' && result.value && Array.isArray(result.value.coverages)) {
                  const processedCoverages = result.value.coverages.map(cov => {
                      let numValue = parseFloat(cov.coverage_done); // Attempt to parse to number
                      let finalValue;

                      if (!isNaN(numValue)) { // If parseable to a number
                          finalValue = parseFloat(numValue.toFixed(2)); // Format to 2 decimal places
                      } else {
                          // If not parseable (e.g., null, undefined, or a non-numeric string)
                          finalValue = cov.coverage_done; // Keep original, or could be set to null or 0 as a default
                      }

                      return {
                          ...cov,
                          coverage_done_original: finalValue,
                          coverage_done_editing: finalValue, // Initialize with consistently formatted value
                          hasChanged: false,
                          isUpdating: false
                      };
                  });
                  return { ...result.value, coverages: processedCoverages };
              }
              return result.value;
          } else {
              if (result.status === 'rejected') {
                   console.error(`Error fetching ${name}:`, result.reason?.message || result.reason);
              } else if (result.value === false) {
                   // For boolean roles, false is a valid fulfilled value, don't log warning unless it's actual data like charts
                   if (name !== 'Rol Platino' && name !== 'Rol Oro') {
                     console.warn(`${name}: Access denied or no data available (API returned false).`);
                   }
              } else {
                   console.warn(`${name}: Invalid data received.`, result.value);
              }
               // Store null if error or no access, except for roles which default to false
               // Don't set global error for missing modules - they will just not display
               console.warn(`${name}: Module not available for this user plan or access denied.`);
              return null;
          }
      },
  
       resetData() {
           this.portfolioDetails = null;
           this.capitalCurveData = null;
           this.portfolioIndexData = null;
           this.riskData = null;
           this.cyclesData = null;
           this.currencyDiverData = null;
           this.profitDiverData = null;
           this.investmentData = null;
           this.sectorsData = null;
           this.countriesData = null;
           this.coveragesData = null;
           // User roles now handled by Vuex store (_g_UserFeatures) - no need to reset
           // Reset analysis and optimization data
           this.portfolioIndexWithAnalysisSymbol = null;
           this.selectedAnalysisStock = null;
           this.tickerSearchQuery = '';
           this.tickerOptions = [];
           this.complementaryValues = [];
           this.complementaryValuesCalculated = false;
           this.complementaryValuesProcessing = false;
           this.complementaryValuesFull = [];
           this.progressBarWidth = 0;
           if (this.optimizePortfolioInterval) clearInterval(this.optimizePortfolioInterval);
           this.optimizePortfolioInterval = null;
           this.progressBarCounter = 0;
           this.countTickers = 0;
           this.fakeTicker = {};

           this.destroyCharts(); // Destroy existing charts before fetching new data
       },
  
       renderAllCharts() {
           // Always render these charts (available for all users)
           this.renderChart('capitalCurve', this.chartIdCapitalCurve, this.getCapitalCurveOptions, this.capitalCurveData);
           

           
           this.renderChart('risk', this.chartIdRisk, this.getRiskOptions, this.riskData);
           
           // Only render advanced charts for non-Bronze users
           if (!this.isUserBronzeOnly) {
               this.renderChart('portfolioIndex', this.chartIdPortfolioIndex, this.getPortfolioIndexOptions, this.portfolioIndexData);
               this.renderChart('cycles', this.chartIdCycles, this.getCyclesOptions, this.cyclesData);
               this.renderChart('investment', this.chartIdInvestment, this.getInvestmentChartOptions, this.investmentData);
               this.renderChart('currencyDiver', this.chartIdCurrencyDiver, this.getPieOptions(this.currencyDiverData, 'Divisa'), this.currencyDiverData);
               this.renderChart('profitDiver', this.chartIdProfitDiver, this.getPieOptions(this.profitDiverData, 'Rango Beneficio'), this.profitDiverData);
               this.renderChart('sectors', this.chartIdSectors, this.getPieOptions(this.sectorsData, 'Sector'), this.sectorsData);
               this.renderChart('countries', this.chartIdCountries, this.getPieOptions(this.countriesData, 'País'), this.countriesData);
           }
       },
  
      renderChart(instanceKey, chartId, optionsCallback, chartData) {
          if (!chartData) {
               console.log(`Skipping render for ${instanceKey} (#${chartId}) due to missing chartData.`);
                if (this.chartInstances[instanceKey]) {
                    try { this.chartInstances[instanceKey].destroy(); } catch(e) { /* ignore */ }
                    this.chartInstances[instanceKey] = null;
                }
               return;
          }

          const chartElement = document.getElementById(chartId);
          if (!chartElement) {
              console.log(`Chart container #${chartId} not found for ${instanceKey} - likely hidden for this user plan.`);
              // Don't throw error for missing containers (they might be hidden for Bronze users)
              return;
          }

          // Destroy previous instance cleanly
          if (this.chartInstances[instanceKey]) {
               try { this.chartInstances[instanceKey].destroy(); } catch(e) { /* ignore */ }
               this.chartInstances[instanceKey] = null;
          }
          
          const options = typeof optionsCallback === 'function' ? optionsCallback() : optionsCallback;

          if (!options) { 
               console.warn(`Skipping render for ${instanceKey} (#${chartId}) due to missing chart options (data might be present but not suitable).`);
               // Re-throw an error if chart options are mandatory for this chart to be considered validly rendered.
               // This ensures that if options are missing and it's an error, the calling function knows.
               // If options can be legitimately missing for some chart states, this throw might be too aggressive.
               // However, if getXOptions() returns null, it usually means data is unsuitable.
               throw new Error(`Missing chart options for ${instanceKey} (#${chartId}).`);
          }
  
          try {
              this.chartInstances[instanceKey] = Highcharts.chart(chartId, options);
          } catch (error) {
              console.error(`Error creating Highcharts instance #${chartId} for ${instanceKey}:`, error);
              if (chartElement) { 
                chartElement.innerHTML = `<div class='no-data-message'>Error al renderizar gráfico ${instanceKey}.</div>`;
              }
              // Re-throw the error so it can be caught by the caller 
              throw error;
          }
      },
  
       // --- Chart Options Getters ---
       getCapitalCurveOptions() {
           if (!this.capitalCurveData || !this.capitalCurveData.series || !this.capitalCurveData.categories) return null;

           const timestamps = this.capitalCurveData.categories.map(cat => {
               const parts = cat.split('-'); // Expects DD-MM-YYYY
               if (parts.length === 3) {
                   // Date.UTC(year, monthIndex (0-11), day)
                   return Date.UTC(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
               }
               console.warn('Invalid date format in capitalCurveData.categories:', cat);
               return null;
           }).filter(ts => ts !== null);

           if (timestamps.length === 0 && this.capitalCurveData.categories.length > 0) {
                console.warn("Capital Curve: Could not parse any valid timestamps from categories.");
                return null; 
           }

           const transformedSeries = this.capitalCurveData.series.map(s => ({
               ...s,
               data: s.data.map((yValue, index) => {
                   if (index < timestamps.length) {
                       return [timestamps[index], yValue];
                   }
                   return null; // Should not happen if categories and data lengths match
               }).filter(point => point !== null)
           }));

           return {
               chart: { type: 'line' },
               xAxis: { 
                   type: 'datetime',
                   dateTimeLabelFormats: {
                       day: '%e %b %Y',
                       week: '%e %b %Y',
                       month: '%b %Y',
                       year: '%Y'
                   }
               },
               yAxis: { title: { text: 'Capital (' + this.portfolioCurrency + ')' } },
               tooltip: { pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:,.2f} ' + this.portfolioCurrency + '</b><br/>', xDateFormat: '%e %b %Y'},
               series: transformedSeries
           };
       },
  
       getPortfolioIndexOptions() {
           if (!this.portfolioIndexData || !this.portfolioIndexData.series || !this.portfolioIndexData.categories) return null;
           
           const timestamps = this.portfolioIndexData.categories.map(cat => {
               const parts = cat.split('-'); // Expects DD-MM-YYYY
               if (parts.length === 3) {
                   return Date.UTC(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
               }
               console.warn('Invalid date format in portfolioIndexData.categories:', cat);
               return null;
           }).filter(ts => ts !== null);

           if (timestamps.length === 0 && this.portfolioIndexData.categories.length > 0) {
                console.warn("Portfolio Index: Could not parse any valid timestamps from categories.");
                return null;
           }

           const transformedSeries = this.portfolioIndexData.series.map(s => ({
               ...s,
               data: s.data.map((yValue, index) => {
                   if (index < timestamps.length) {
                       return [timestamps[index], yValue];
                   }
                   return null;
               }).filter(point => point !== null)
           }));
           
           // Add reference to portfolioIndexData for portfolioStats
           const portfolioIndexData = this.portfolioIndexData;
           return {
               chart: { type: 'line' },
               xAxis: { 
                   type: 'datetime',
                   dateTimeLabelFormats: {
                       day: '%e %b %Y',
                       week: '%e %b %Y',
                       month: '%b %Y',
                       year: '%Y'
                   }
               },
               yAxis: { title: { text: 'Índice (Base 1)' } },
               tooltip: {
                   pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:,.4f}</b><br/>',
                   xDateFormat: '%e %b %Y',
               },
               series: transformedSeries,
               portfolioStats: {
                   profit: portfolioIndexData.profit,
                   max_drawdown: portfolioIndexData.max_drawdown,
                   profit_analysis: portfolioIndexData.profit_analysis,
                   max_drawdown_analysis: portfolioIndexData.max_drawdown_analysis
               }
           };
       },
  
       getRiskOptions() {
           if (!this.riskData || !Array.isArray(this.riskData) || this.riskData.length === 0) {
               console.warn("Risk Chart: Initial riskData is empty or not an array.");
               return null;
           }

           const capturedFormatCurrency = this.formatCurrencyRight;
           const capturedPortfolioCurrency = this.portfolioCurrency;

           // Strengthen the filter for validRiskData
           const validRiskData = this.riskData.filter(item => 
               item && 
               typeof item.name === 'string' && item.name.trim() !== '' && 
               item.data && item.data[0] && 
               typeof item.data[0].y === 'number' && 
               typeof item.data[0].value === 'number'
           );

           if (validRiskData.length === 0) {
               console.warn("Risk Chart: No valid data items after filtering. Original riskData:", JSON.parse(JSON.stringify(this.riskData)));
               return null; 
           }

           const categories = validRiskData.map(s => s.name);
           // console.log("Risk Chart Categories being set:", categories); // Optional: for debugging categories

           return {
               chart: { type: 'column' },
               xAxis: { 
                   categories: categories,
                   labels: { 
                       rotation: -45,
                       style: { fontSize: '10px' }
                    } 
                },
               yAxis: { title: { text: 'Riesgo (%)' } },
               tooltip: {
                   // Formatter adapted for shared tooltip context (which is global default)
                   formatter: function() {
                       if (!this.points || this.points.length === 0) {
                           return `Error: No points in shared tooltip. Key: ${this.key}, X: ${this.x}`;
                       }

                       const pointProxy = this.points[0]; // Data for the first (and only) series in this chart
                       const categoryName = pointProxy.key; // Category name from the point itself
                       const yValue = pointProxy.y;
                       const seriesName = pointProxy.series.name;

                       if (typeof categoryName === 'undefined') {
                           // This case should ideally not be hit if categories and data are well-formed
                           console.error("Risk Chart Tooltip: Category name is undefined.", {globalKey: this.key, globalX: this.x, pointProxy });
                           return `Error: Categoría no definida`; 
                       }

                       const originalPoint = validRiskData.find(item => item.name === categoryName);
                       if (originalPoint && originalPoint.data && originalPoint.data[0]) {
                           const absoluteData = originalPoint.data[0];
                           const valueForFormatting = absoluteData.value;
                           let formattedAbsolute = 'N/A'; // Default if not a number or not found

                           if (typeof valueForFormatting === 'number') {
                               formattedAbsolute = capturedFormatCurrency(valueForFormatting, capturedPortfolioCurrency);
                           }

                           return `<b>${categoryName}</b><br/>` +
                                  `${seriesName}: ${Highcharts.numberFormat(yValue, 2)}%<br/>` +
                                  `Absoluto: ${formattedAbsolute}`;
                       } else {
                           // Fallback if originalPoint or its data is not found, though categories should align with validRiskData
                           console.warn("Risk Chart Tooltip: Original point data not found for category:", categoryName, {validRiskData});
                           return `<b>${categoryName}</b><br/>` +
                                  `${seriesName}: ${Highcharts.numberFormat(yValue, 2)}%<br/>` +
                                  `Absoluto: N/A`;
                       }
                   }
               },
               plotOptions: {
                   column: {
                       dataLabels: {
                           enabled: true,
                           format: '{point.y:.1f}%',
                           // style set by global options
                       },
                       // colorByPoint: true, // If you want each column to have a different color from the palette
                   }
               },
               legend: { enabled: false }, // Only one series, legend not very useful
               series: [{
                   name: 'Riesgo', // Single series
                   data: validRiskData.map(item => ({
                       name: item.name, // for tooltip matching if needed, and for dataLabel context
                       y: item.data[0].y
                       // color: can be set here if needed per point
                   }))
               }]
           };
       },
  
        getCyclesOptions() {
            if (!this.cyclesData || !this.cyclesData.series) return null;
            // API data: { series: [{ name: "Cicle up", data: [v1, v2...], stack: 'up' }], categorias: ["Y/W", ...] }
            return {
                chart: { type: 'column' },
                xAxis: { categories: this.cyclesData.categorias },
                yAxis: { title: { text: 'Beneficio / Pérdida (' + this.portfolioCurrency + ')' } },
                tooltip: {
                   pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:,.2f} ' + this.portfolioCurrency + '</b><br/>',
                   footerFormat: 'Total: {point.total:,.2f} ' + this.portfolioCurrency
                },
                plotOptions: {
                    column: { stacking: 'normal' } // Enable stacking
                },
                series: this.cyclesData.series
            };
        },
  
        getInvestmentChartOptions() {
            if (!this.investmentData) return null;
            const self = this; // For access within formatter if needed, or use this.portfolioCurrency directly
            const legacyColors = Highcharts.getOptions().colors; // Use the global palette

            // Ensure investmentData has the expected structure from portfolio-general.js
            // This is a complex nested pie chart
            return {
                chart: { type: 'pie' },
                title: { text: null }, // Already global
                tooltip: {
                     valueSuffix: ' %',
                     // More specific tooltip might be needed depending on how data is structured for Highcharts
                     // For the outer ring, it might show absolute value
                     formatter: function() {
                        let tooltip = `<b>${this.series.name}</b><br/>${this.point.name}: ${Highcharts.numberFormat(this.y, 2)}%`;
                        if (this.point.value) { // Assuming 'value' holds the absolute amount
                            tooltip += `<br/>Valor: ${self.formatCurrencyRight(this.point.value, self.portfolioCurrency)}`;
                        }
                        return tooltip;
                    }
                },
                plotOptions: {
                    pie: {
                        shadow: false,
                        center: ['50%', '50%'],
                         dataLabels: {
                            // distance: -30, // Example, adjust as needed
                            // color: 'white', // Example
                            // style: { textOutline: 'none' }
                         }
                    }
                },
                series: [
                    {
                        name: 'Teórico',
                        data: [
                            { name: 'Acción', y: this.investmentData.stock_total, color: legacyColors[0] },
                            { name: 'CFD', y: this.investmentData.cfd_total, color: legacyColors[1] },
                            { name: 'Efectivo', y: this.investmentData.cash_total, color: legacyColors[2] }
                        ],
                        size: '60%',
                        dataLabels: {
                            formatter: function() {
                                return this.y > 5 ? this.point.name : null;
                            },
                            color: '#ffffff',
                            distance: -35, // Adjust for better visibility
                            style: { textOutline: 'none', fontWeight: 'bold' }
                        }
                    },
                    {
                        name: 'Detalle', // Name for the outer ring series
                        data: [
                            { name: 'Acción Invertido', y: this.investmentData.stock_investment, value: this.investmentData.stock_investment_absolute, color: Highcharts.color(legacyColors[0]).brighten(0.1).get() },
                            { name: 'Acción Libre', y: this.investmentData.stock_free, value: this.investmentData.stock_free_absolute, color: Highcharts.color(legacyColors[0]).brighten(0.2).get() },
                            { name: 'CFD Invertido', y: this.investmentData.cfd_investment, value: this.investmentData.cfd_investment_absolute, color: Highcharts.color(legacyColors[1]).brighten(0.1).get() },
                            { name: 'CFD Libre', y: this.investmentData.cfd_free, value: this.investmentData.cfd_free_absolute, color: Highcharts.color(legacyColors[1]).brighten(0.2).get() },
                            { name: 'Efectivo Cubierto', y: this.investmentData.cash_investment, value: this.investmentData.cash_investment_absolute, color: Highcharts.color(legacyColors[2]).brighten(0.1).get() },
                            { name: 'Efectivo Libre', y: this.investmentData.cash_free, value: this.investmentData.cash_free_absolute, color: Highcharts.color(legacyColors[2]).brighten(0.2).get() }
                        ],
                        size: '80%',
                        innerSize: '60%',
                        dataLabels: {
                            formatter: function() {
                                // Show label if percentage is > 1%
                                return this.y > 1 ? `<b>${this.point.name}:</b> ${Highcharts.numberFormat(this.y,1)}%` : null;
                            },
                            // color: '#000000', // Default, adjust if needed
                            distance: 15, // Position labels outside
                            style: { textOutline: 'none' } // Ensure readability
                        }
                    }
                ]
            };
        },
  
        getPieOptions(data, seriesName) {
            if (!data || !Array.isArray(data) || data.length === 0 || !data[0].data) return null;
            // API data: [{ name: "% invested", data: [ ['Name', perc], ... ] }]
            // OR: [{ name: "% invested", data: [ { name: "Currency", y: perc, value: amount }, ... ] }]
             return {
                 chart: { type: 'pie' },
                 tooltip: {
                      // Check if 'value' exists for amount display
                     pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>' +
                                  '{#if point.value}<br/>Valor: {point.value:,.2f} ' + this.portfolioCurrency + '{/if}'
                 },
                 series: [{
                     name: seriesName || data[0].name, // Use provided name or API name
                     colorByPoint: true,
                     data: data[0].data
                 }]
             };
         },
  
  
      // --- Lifecycle Hooks ---
      destroyCharts() {
        console.log("Destroying General charts");
        for (const key in this.chartInstances) {
          if (this.chartInstances[key]) {
            try { this.chartInstances[key].destroy(); } catch(e) { /* ignore */ }
            this.chartInstances[key] = null;
          }
        }
      },

      // Method to handle input change for coverage
      prepareCoverageUpdate(coverageItem, event) {
        const currentInputElementValue = event.target.value;
        let sanitizedString = '';

        if (currentInputElementValue === null || currentInputElementValue === undefined || String(currentInputElementValue).trim() === '') {
            sanitizedString = ''; // Handle empty or whitespace only
        } else {
            const valueStr = String(currentInputElementValue);
            let parts = valueStr.split('.');
            let integerPart = parts[0].replace(/[^0-9]/g, ''); 

            sanitizedString = integerPart;

            if (parts.length > 1) { // If there's a decimal part detected
                sanitizedString += '.'; // Add the dot
                if (parts[1]) { // If there are characters after the dot
                    let decimalPart = parts[1].replace(/[^0-9]/g, '');
                    sanitizedString += decimalPart.substring(0, 2); // Add max 2 decimal digits
                }
            }
        }
        
        // Update the model if the sanitization changed the value.
        // This ensures the input field reflects the sanitized value.
        if (coverageItem.coverage_done_editing !== sanitizedString) {
            coverageItem.coverage_done_editing = sanitizedString;
        }

        // Determine hasChanged based on the numeric value of the sanitized string
        let numericValueCurrent = parseFloat(sanitizedString);
        
        if (sanitizedString === '') { // Empty string treated as 0 for comparison
            numericValueCurrent = 0;
        } else if (sanitizedString === '.' || isNaN(numericValueCurrent) ) { // Just a dot or unparseable (e.g. if sanitization leads to ".-")
            // If it's just a dot, it implies change unless original was also effectively a dot (unlikely)
            coverageItem.hasChanged = String(coverageItem.coverage_done_original) !== '.';
            return; // Exit, as it's an intermediate state not yet a full number
        }
        
        // coverage_done_original is already a 2-decimal number from data load process
        // Compare current numeric value (rounded again to ensure strict 2-decimal comparison) with original
        coverageItem.hasChanged = parseFloat(numericValueCurrent.toFixed(2)) !== coverageItem.coverage_done_original;
      },

      async executeUpdateCoverage(coverageItem) {
          if (!coverageItem.hasChanged || coverageItem.isUpdating) return;

          let valueToSaveStr = String(coverageItem.coverage_done_editing);
          let valueToSave = parseFloat(valueToSaveStr);

          // Default to 0 if input is empty, just a dot, or not a valid number after parsing
          if (valueToSaveStr.trim() === '' || valueToSaveStr === '.' || isNaN(valueToSave)) {
              valueToSave = 0; 
          }
          
          valueToSave = parseFloat(valueToSave.toFixed(2)); // Ensure it's rounded to two decimal places before saving

          coverageItem.isUpdating = true;
          this.coverageUpdateStatus = { message: '', type: '' };
          try {
              await apiService.updateCoverage(this.portfolioId, coverageItem.currency, valueToSave);
              coverageItem.coverage_done_original = valueToSave; // Update original with the saved (rounded) value
              coverageItem.hasChanged = false; // Reset changed state
              console.log(`Coverage for ${coverageItem.currency} updated to ${valueToSave}`);
              this.coverageUpdateStatus = { message: `Cobertura para ${coverageItem.currency} actualizada.`, type: 'success' };
              await this.fetchCoverages(); // Fetch only coverages data
          } catch (err) {
              console.error("Error updating coverage:", err);
              coverageItem.coverage_done_editing = coverageItem.coverage_done_original; // Revert on error
              coverageItem.hasChanged = false;
              this.coverageUpdateStatus = { message: `Error actualizando ${coverageItem.currency}: ${err.message}`, type: 'error' };
          } finally {
              coverageItem.isUpdating = false;
              setTimeout(() => { this.coverageUpdateStatus = { message: '', type: '' }; }, 4000); // Clear status after 4s
          }
      },

      async fetchCoverages() {
        try {
            const newCoveragesData = await apiService.getCoveragesData(this.portfolioId);
            if (newCoveragesData && Array.isArray(newCoveragesData.coverages)) {
                const processedCoverages = newCoveragesData.coverages.map(cov => {
                    let numValue = parseFloat(cov.coverage_done); // Attempt to parse to number
                    let finalValue;

                    if (!isNaN(numValue)) { // If parseable to a number
                        finalValue = parseFloat(numValue.toFixed(2)); // Format to 2 decimal places
                    } else {
                        // If not parseable (e.g., null, undefined, or a non-numeric string)
                        finalValue = cov.coverage_done; // Keep original, or could be set to null or 0 as a default
                    }

                    return {
                        ...cov,
                        coverage_done_original: finalValue,
                        coverage_done_editing: finalValue,
                        hasChanged: false,
                        isUpdating: false
                    };
                });
                this.coveragesData = { 
                    ...newCoveragesData, 
                    coverages: processedCoverages 
                };
            } else {
                console.warn("Coverages data after update is invalid:", newCoveragesData);
                this.coverageUpdateStatus = { message: 'Error: Datos de cobertura inválidos tras actualizar.', type: 'error' };
            }
        } catch (err) {
            console.error("Error refetching coverages data:", err);
            this.coverageUpdateStatus = { message: `Error refrescando datos de cobertura: ${err.message}`, type: 'error' };
        } finally {
            // Clear error/info message related to coverage refresh after a delay, only if it was set by this method
            if (this.coverageUpdateStatus.message.includes('cobertura') || this.coverageUpdateStatus.message.includes('Coverages data')) {
                 setTimeout(() => { 
                    if (this.coverageUpdateStatus.message.includes('cobertura') || this.coverageUpdateStatus.message.includes('Coverages data')) {
                        this.coverageUpdateStatus = { message: '', type: '' }; 
                    }
                }, 4000);
            }
        }
    },
    },
    watch: {
      portfolioId: {
        immediate: true,
        handler(newId) {
          if (newId) {
            this.fetchData();
          } else {
            this.resetData();
            this.error = null; // Clear error when portfolio deselected
          }
        },
      },
    },
     mounted() {
         // Data fetching is handled by the watcher
         // Optional: Set up resize handlers if needed, Highcharts often handles basic resizing
     },
    beforeDestroy() {
      this.destroyCharts();
      // Remove resize listeners if added
    },
  };
  </script>
  
  <style scoped>
  a {
    text-decoration: none;
    color: var(--color-highlight);
  }

  .general-container {
      padding: 16px;
      max-width: 100%;
      overflow-x: hidden;
      width: 100%;
      box-sizing: border-box;
  }
  
  .charts-grid {
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;
      box-sizing: border-box;
  }
  
  .chart-wrapper {
      background-color: var(--color-surface, #2c2c2c);
      border: 1px solid var(--color-outline, #444);
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      width: 100%;
      box-sizing: border-box;
      color: var(--color-text-primary);
      overflow: hidden;
  }
  
  .chart-wrapper h3 {
      margin-top: 0;
      margin-bottom: 16px;
      font-size: 1.15rem;
      color: var(--color-text-title, var(--color-text-primary, #E0E0E0));
      text-align: center;
      border-bottom: 1px solid var(--color-outline-soft, rgba(255,255,255,0.1));
      padding-bottom: 10px;
      font-weight: 600;
  }
  
  .chart-container {
      flex-grow: 1;
      width: 100%;
      height: 300px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
  }
  
  /* Responsive chart heights */
  .chart-container-small { 
      height: 250px;
  }
  .chart-container-medium { 
      height: 300px;
  }
  .chart-container-large { 
      height: 350px;
  }
  
  /* Responsive adjustments for chart heights */
  @media (max-width: 767px) {
      .chart-container {
          height: 250px;
      }
      .chart-container-small { 
          height: 200px;
      }
      .chart-container-medium { 
          height: 250px;
      }
      .chart-container-large { 
          height: 280px;
      }
  }

  @media (min-width: 768px) and (max-width: 1199px) {
      .chart-container {
          height: 280px;
      }
      .chart-container-small { 
          height: 230px;
      }
      .chart-container-medium { 
          height: 280px;
      }
      .chart-container-large { 
          height: 320px;
      }
  }

  .no-data-message {
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-text-muted, #888);
      font-style: italic;
      text-align: center;
      padding: 20px;
      min-height: 100px;
  }

  .no-data-message.small-text {
      font-size: 0.85rem;
      padding: 10px;
      min-height: unset;
  }

  .no-data-message.full-width {
      grid-column: 1 / -1;
      background-color: var(--color-background-soft, #f9f9f9);
      border-radius: 5px;
      margin-top: 10px;
  }

  /* Loading indicator */
  .loading-indicator-gcn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      font-size: 1.1rem;
      color: var(--color-text-secondary);
  }

  /* Error message */
  .error-message-gcn {
      padding: 1rem;
      margin: 1rem 0;
      color: var(--color-error, #dc3545);
      background-color: rgba(var(--color-error-rgb, 220, 53, 69), 0.1);
      border-radius: 4px;
      border-left: 4px solid var(--color-error, #dc3545);
  }
  
  /* Investment Distribution Specific Styles */
  .investment-summary-details {
      font-size: 0.9rem;
      line-height: 1.5;
      padding: 16px;
      background-color: var(--color-background-soft, rgba(255,255,255,0.03));
      border-radius: 6px;
      margin-top: 16px;
      color: var(--color-text-secondary);
  }

  .investment-summary-details hr {
      border: none;
      border-top: 1px solid var(--color-outline-soft, rgba(255,255,255,0.1));
      margin: 10px 0;
  }

  .investment-summary-details strong {
      color: var(--color-text-strong, var(--color-text-primary));
  }

  .investment-row {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      margin-bottom: 8px;
  }

  @media (min-width: 768px) {
      .investment-row {
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      }
  }

  /* --- Table Styles --- */
  .table-wrapper {
      overflow-x: auto;
      margin-bottom: 20px;
      border: 1px solid var(--color-outline);
      border-radius: 6px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      width: 100%;
      box-sizing: border-box;
  }

  .data-table {
      width: 100%;
      min-width: 100%;
      border-collapse: collapse;
      font-size: 0.9rem;
      table-layout: auto;
  }

  /* Make compact tables more readable on mobile */
  .compact-table {
      font-size: 0.85rem;
  }

  .data-table th,
  .data-table td {
      border: 1px solid var(--color-outline-soft, rgba(255,255,255,0.15));
      padding: 8px 12px;
      text-align: left;
      vertical-align: middle;
      color: var(--color-text-primary);
      line-height: 1.4;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
  }

  .data-table th {
      background-color: var(--color-surface-header, rgba(255,255,255,0.05));
      font-weight: 600;
      color: var(--color-text-secondary);
      text-align: center;
      font-size: 0.9em;
      white-space: nowrap;
      min-width: 80px;
  }

  .data-table input[type="text"],
  .data-table input[type="number"] {
      width: 100%;
      max-width: 100px;
      min-width: 60px;
      padding: 6px 8px;
      border: 1px solid var(--color-input-border, var(--color-outline, #555));
      border-radius: 4px;
      background-color: var(--color-input-background, var(--color-surface, #333));
      color: var(--color-text-input, var(--color-text-primary, #eee));
      font-size: 0.9em;
      box-sizing: border-box;
      -moz-appearance: textfield; /* Firefox */
  }

  /* Responsive table adjustments */
  @media (max-width: 767px) {
      .data-table {
          font-size: 0.8rem;
      }
      .data-table th,
      .data-table td {
          padding: 6px 8px;
          font-size: inherit;
      }
      .compact-table {
          font-size: 0.75rem;
      }
      .data-table th {
          font-size: 0.8em;
          white-space: normal;
          word-wrap: break-word;
          min-width: 60px;
      }
      .complementary-values-table-wrapper .data-table td,
      .complementary-values-table-wrapper .data-table th {
          padding: 5px 6px;
          font-size: 0.75em;
      }
      .ticker-input-group,
      .optimize-portfolio-container {
          padding: 12px;
          gap: 10px;
      }
      .ticker-input-controls input,
      .btnGcn {
          font-size: 0.85rem;
      }
      .btnGcn-sm {
          font-size: 0.8rem;
      }
  }

  .data-table tbody tr:nth-child(even) {
      background-color: var(--color-background-zebra, rgba(0,0,0,0.02));
  }

  .data-table tbody tr:hover {
      background-color: var(--color-surface-highlight, rgba(255,255,255,0.07));
  }

  .data-table input[type="text"]:focus,
  .data-table input[type="number"]:focus {
      border-color: var(--color-highlight);
      outline: none;
      box-shadow: 0 0 0 2px rgba(var(--color-highlight-rgb), 0.2);
  }

    .complementary-values-table-wrapper .data-table td,
    .complementary-values-table-wrapper .data-table th {
        padding: 5px 6px; /* Tighter padding */
        font-size: 0.75em; /* Ensure consistency */
    }

    .ticker-input-group,
    .optimize-portfolio-container {
        padding: 12px;
        gap: 10px;
    }
    .ticker-input-controls input,
    .btnGcn {
        font-size: 0.85rem; /* Slightly smaller inputs/buttons */
    }
    .btnGcn-sm {
        font-size: 0.8rem;
    }

  .data-table input[type="number"]::-webkit-outer-spin-button,
  .data-table input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .data-table input[type="text"]:focus,
  .data-table input[type="number"]:focus {
      border-color: var(--color-highlight);
      outline: none;
      box-shadow: 0 0 0 2px rgba(var(--color-highlight-rgb), 0.2);
  }

  .data-table .text-right { text-align: right; }
  .data-table .text-center { text-align: center; }
  .data-table .text-success { color: var(--color-success); }
  .data-table .text-danger { color: var(--color-error); }

  .data-table th.sortable {
      cursor: pointer;
  }

  .data-table th.sortable:hover {
      background-color: var(--color-surface-hover, rgba(255,255,255,0.08));
  }

  .data-table th .sort-icon {
      font-size: 0.8em;
      margin-left: 4px;
  }

  /* --- Autocomplete & Optimize Portfolio Styles --- */
  .ticker-input-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
      background-color: var(--color-surface-soft, rgba(255,255,255,0.03));
      padding: 15px;
      border-radius: 6px;
      border: 1px solid var(--color-outline-soft, rgba(255,255,255,0.1));
      width: 100%;
      box-sizing: border-box;
  }

  .ticker-search-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
      flex-grow: 1;
  }

  .ticker-input-controls {
      display: flex;
      width: 100%;
      box-sizing: border-box;
  }

  .ticker-input-controls input {
      flex: 1;
      min-width: 0;
      padding: 8px 10px;
      border: 1px solid var(--color-input-border, var(--color-outline, #555));
      border-radius: 4px;
      background-color: var(--color-input-background, var(--color-surface, #333));
      color: var(--color-text-input, var(--color-text-primary, #eee));
      transition: border-color 0.2s ease;
      box-sizing: border-box;
  }

  .ticker-input-controls input:focus {
      border-color: var(--color-highlight);
      outline: none;
      box-shadow: 0 0 0 2px rgba(var(--color-highlight-rgb), 0.2);
  }

  .ticker-input-controls input.input-error {
      border-color: var(--color-error);
      background-color: rgba(var(--color-error-rgb, 220, 53, 69), 0.05);
  }

  .ticker-button-group {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      width: 100%;
  }

  .action-feedback {
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 0.9em;
      text-align: left;
      animation: fadeIn 0.3s ease;
      word-wrap: break-word;
  }

  @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-5px); }
      to { opacity: 1; transform: translateY(0); }
  }

  .action-feedback.success {
      background-color: rgba(var(--color-success-rgb, 40, 167, 69), 0.15);
      border: 1px solid rgba(var(--color-success-rgb, 40, 167, 69), 0.3);
      color: var(--color-success, #28a745);
  }

  .action-feedback.error {
      background-color: rgba(var(--color-error-rgb, 220, 53, 69), 0.15);
      border: 1px solid rgba(var(--color-error-rgb, 220, 53, 69), 0.3);
      color: var(--color-error, #dc3545);
  }

  .action-feedback.info {
      background-color: rgba(var(--color-info-rgb, 23, 162, 184), 0.15);
      border: 1px solid rgba(var(--color-info-rgb, 23, 162, 184), 0.3);
      color: var(--color-info, #17a2b8);
  }

  /* Progress bar */
  .progress-bar-container {
      width: 100%;
      background-color: var(--color-surface-progress, var(--color-outline, #444));
      border-radius: 4px;
      overflow: hidden;
      height: 10px;
      margin-top: 8px;
      box-sizing: border-box;
  }

  .progress-bar {
      height: 100%;
      background-color: var(--color-highlight);
      transition: width 0.3s ease-in-out;
      text-align: center;
      color: white;
      font-size: 0.75em;
      line-height: 10px;
  }

  /* Button Styles */
  .btnGcn {
      padding: 8px 15px;
      font-size: 0.9rem;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid transparent;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      flex-shrink: 0;
      white-space: nowrap;
      box-sizing: border-box;
      width: 100%;
      justify-content: center;
  }

  .btnGcn-sm {
      padding: 5px 10px;
      font-size: 0.85rem;
  }

  .btnGcn-primary {
      background-color: var(--color-highlight);
      color: var(--color-text-on-highlight, #fff);
      border-color: var(--color-highlight);
  }

  .btnGcn-secondary {
      background-color: var(--color-surface-button, #555);
      color: var(--color-text-on-surface-button, #fff);
      border-color: var(--color-outline-strong, #666);
  }

  .btnGcn-primary:hover, .btnGcn-secondary:hover {
      filter: brightness(1.1);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .btnGcn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      background-color: var(--color-muted, #666);
      border-color: var(--color-muted, #666);
      color: var(--color-text-primary);
      box-shadow: none;
      filter: grayscale(0.5);
  }

  .btnGcn-link {
      background: transparent;
      color: var(--color-highlight);
      border: none;
      padding: 4px;
  }

  .btnGcn-link:hover {
      color: var(--color-highlight-hover);
      background-color: rgba(var(--color-highlight-rgb), 0.1);
      box-shadow: none;
  }

  .btnGcn .material-icons-outlined {
      font-size: 1.1em;
  }

  /* Icon animations */
  .icon-spin {
      animation: icon-spin 2s infinite linear;
      display: inline-block;
  }

  @keyframes icon-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
  }

  /* New styles for row wrappers */
  .row-wrapper-for-children {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    box-sizing: border-box;
  }

  .row-2-items-per-row {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .row-3-items-per-row {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* Responsive layouts for row wrappers */
  @media (min-width: 768px) {
    .row-2-items-per-row {
      flex-direction: row;
      gap: 20px;
    }
    .row-2-items-per-row > .chart-wrapper {
      flex: 1;
      min-width: 0;
    }
  }

  @media (min-width: 768px) {
    .row-3-items-per-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }
  }

  @media (min-width: 1200px) {
    .row-3-items-per-row {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  /* Styles for Portfolio Index two-column layout */
  .portfolio-index-layout {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    box-sizing: border-box;
  }

  .portfolio-index-left-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
  }

  .portfolio-index-right-panel {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }
  
  .portfolio-index-right-panel .chart-container,
  .portfolio-index-right-panel .no-data-message {
    flex-grow: 1;
    min-height: 500px;
  }

  @media (min-width: 992px) {
    .portfolio-index-layout {
      flex-direction: row;
      align-items: stretch;
    }
    .portfolio-index-left-panel {
      flex: 0 0 35%;
      max-width: 35%;
    }
    .portfolio-index-right-panel {
      flex: 1;
      min-width: 0;
    }
    .portfolio-index-right-panel .chart-container,
    .portfolio-index-right-panel .no-data-message {
      flex-grow: 1;
      min-height: 500px;
    }
  }

  /* Optimize Portfolio Styles */
  .optimize-portfolio-container {
      display: flex;
      flex-direction: column;
      gap: 15px;
      background-color: var(--color-surface-soft, rgba(255,255,255,0.03));
      padding: 15px;
      border-radius: 6px;
      border: 1px solid var(--color-outline-soft, rgba(255,255,255,0.1));
      width: 100%;
      box-sizing: border-box;
  }

  .optimize-portfolio-container h4 {
      margin: 0 0 10px 0;
      font-size: 1em;
      font-weight: 600;
      color: var(--color-text-primary);
      padding-bottom: 8px;
      border-bottom: 1px solid var(--color-outline-soft, rgba(255,255,255,0.1));
  }

  .optimize-portfolio-controls {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      flex-grow: 1;
  }

  .complementary-values-wrapper {
      display: flex;
      flex-direction: column;
      max-height: 400px;
      width: 100%;
      box-sizing: border-box;
  }

  .complementary-values-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      flex-wrap: wrap;
      gap: 8px;
  }

  .complementary-values-header h4 {
      margin: 0;
      padding: 0;
      border: none;
      font-size: 0.95em;
      color: var(--color-text-secondary);
  }

  .complementary-values-count {
      font-size: 0.85em;
      color: var(--color-text-muted);
      background-color: var(--color-surface, rgba(0,0,0,0.2));
      padding: 2px 8px;
      border-radius: 10px;
      flex-shrink: 0;
  }

  .complementary-values-table-wrapper {
      overflow: auto;
      max-height: 350px;
      border: 1px solid var(--color-outline);
      border-radius: 6px;
      width: 100%;
      box-sizing: border-box;
  }

  /* Table column widths - more flexible */
  .data-table .col-ticker { min-width: 70px; }
  .data-table .col-type { min-width: 50px; }
  .data-table .col-ratio { min-width: 70px; }
  .data-table .col-profit { min-width: 60px; }
  .data-table .col-dd { min-width: 60px; }
  .data-table .col-action { min-width: 50px; }

  /* More compact table */
  .complementary-values-table-wrapper .data-table td,
  .complementary-values-table-wrapper .data-table th {
      padding: 6px 8px;
      font-size: 0.85em;
      white-space: nowrap;
  }

  .complementary-values-table-wrapper .data-table thead {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: var(--color-surface-header, rgba(255,255,255,0.05));
  }

  /* Progress bar enhancements */
  .progress-bar-container {
      height: 8px;
      border-radius: 4px;
      background-color: var(--color-surface-progress, rgba(255,255,255,0.1));
      overflow: hidden;
  }

  .progress-bar {
      height: 100%;
      border-radius: 4px;
      background-color: var(--color-highlight);
      color: transparent;
      font-size: 0;
      text-align: center;
      transition: width 0.3s ease-out;
  }

  .progress-bar[style*="width: 2"] {
      font-size: 8px;
      line-height: 8px;
      color: var(--color-text-on-highlight, white);
  }

  /* Mobile specific adjustments */
  @media (max-width: 767px) {
    .general-container {
      padding: 12px;
    }

    .charts-grid {
      gap: 16px;
    }

    .chart-wrapper {
      padding: 12px;
    }

    .chart-wrapper h3 {
      font-size: 1rem;
      margin-bottom: 12px;
      padding-bottom: 8px;
    }

    .portfolio-index-left-panel {
      gap: 12px;
      justify-content: flex-start;
    }

    .portfolio-section {
        padding: 12px;
        flex: none;
    }

    .portfolio-section h4 {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .premium-badge {
        font-size: 0.65em;
        padding: 3px 6px;
    }

    .premium-badge .material-icons-outlined {
        font-size: 12px;
    }

    .ticker-input-controls input,
    .btnGcn {
        font-size: 0.85rem;
    }

    .btnGcn-sm {
        font-size: 0.8rem;
        padding: 4px 8px;
    }

    .ticker-button-group {
        gap: 6px;
    }

    .investment-summary-details {
        padding: 12px;
        font-size: 0.85rem;
    }

    .complementary-values-table-wrapper .data-table td,
    .complementary-values-table-wrapper .data-table th {
        padding: 4px 6px;
        font-size: 0.75em;
    }

    .complementary-values-header {
        flex-direction: column;
        align-items: flex-start;
    }
  }

  /* Medium screen adjustments */
  @media (min-width: 768px) and (max-width: 1199px) {
    .general-container {
      padding: 14px;
    }

    .charts-grid {
      gap: 18px;
    }

    .chart-wrapper {
      padding: 14px;
    }

    .ticker-input-group,
    .optimize-portfolio-container {
        padding: 14px;
    }
  }

  /* Ensure all elements use box-sizing border-box */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  /* Prevent horizontal overflow at the root level */
  .general-container * {
    max-width: 100%;
  }

  /* Special handling for investment distribution to prevent overflow */
  .investment-distribution {
    width: 100%;
    overflow: hidden;
  }

  .investment-distribution .chart-container {
    width: 100%;
    overflow: hidden;
  }

  /* Ensure portfolio analysis section adapts properly */
  .portfolio-analysis-section {
    width: 100%;
    overflow: hidden;
  }

  .portfolio-analysis-section .chart-container {
    width: 100%;
    overflow: hidden;
  }

  .btnGcn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      background-color: var(--color-muted, #666);
      border-color: var(--color-muted, #666);
      color: var(--color-text-primary);
      box-shadow: none;
      filter: grayscale(0.5);
  }

  .btnGcn-processing {
      background-color: var(--color-warning, #ffc107) !important;
      border-color: var(--color-warning, #ffc107) !important;
      color: var(--color-text-on-warning, #000) !important;
      opacity: 1 !important;
      cursor: wait !important;
  }

  .btnGcn-processing:hover {
      background-color: var(--color-warning-hover, #e0a800) !important;
      filter: none !important;
  }

  .btnGcn-completed {
      background-color: var(--color-success, #28a745) !important;
      border-color: var(--color-success, #28a745) !important;
      color: var(--color-text-on-success, #fff) !important;
      opacity: 1 !important;
      cursor: default !important;
  }

  .btnGcn-completed:hover {
      background-color: var(--color-success-hover, #218838) !important;
      filter: none !important;
  }

  /* Portfolio section standardized spacing */
  .portfolio-section {
    padding: 15px;
    width: 100%;
    box-sizing: border-box;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* Premium sections styling */
  .premium-section {
    background-color: var(--color-surface-soft, rgba(255,255,255,0.02));
    border-radius: 8px;
  }

  .premium-gold {
    border-left: 3px solid rgba(255, 215, 0, 0.3);
  }

  .premium-platinum {
    border-left: 3px solid rgba(192, 192, 192, 0.3);
  }

  .portfolio-section h4 {
    margin: 0 0 15px 0;
    font-size: 1em;
    font-weight: 600;
    color: var(--color-text-primary);
    padding-bottom: 8px;
    border-bottom: 1px solid var(--color-outline-soft, rgba(255,255,255,0.1));
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* Premium badges */
  .premium-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 0.7em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .premium-badge .material-icons-outlined {
    font-size: 14px;
  }

  .premium-badge-gold {
    background: linear-gradient(135deg, #FFD700, #FFA500);
    color: #000;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
  }

  .premium-badge-platinum {
    background: linear-gradient(135deg, #E5E4E2, #C0C0C0);
    color: #000;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    box-shadow: 0 2px 8px rgba(192, 192, 192, 0.3);
  }

  .premium-badge:hover {
    transform: scale(1.05);
    transition: transform 0.2s ease;
  }

  /* Premium disabled state */
  .premium-disabled {
    opacity: 0.6;
    position: relative;
  }

  .premium-disabled::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(0, 0, 0, 0.1) 10px,
      rgba(0, 0, 0, 0.1) 20px
    );
    pointer-events: none;
    z-index: 1;
    border-radius: 8px;
  }

  .premium-disabled input,
  .premium-disabled button,
  .premium-disabled .data-table {
    pointer-events: none;
  }

  /* Premium upgrade message */
  .premium-upgrade-message {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.1));
    border: 1px solid rgba(255, 215, 0, 0.3);
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 15px;
    position: relative;
    z-index: 2;
  }

  .premium-upgrade-message p {
    margin: 0;
    color: var(--color-text-primary);
    font-size: 0.9em;
    line-height: 1.4;
  }

  .premium-upgrade-message.small-text {
    padding: 8px;
    margin-bottom: 10px;
  }

  .premium-upgrade-message.small-text p {
    font-size: 0.8em;
  }

  .premium-platinum .premium-upgrade-message {
    background: linear-gradient(135deg, rgba(192, 192, 192, 0.1), rgba(169, 169, 169, 0.1));
    border-color: rgba(192, 192, 192, 0.3);
  }

  .portfolio-section .table-wrapper {
    margin: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .portfolio-section .action-feedback {
    margin-top: 10px;
  }

  .portfolio-section .data-table {
    flex-grow: 1;
  }
  </style>